import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { formatCurrency } from "../../core/currency.core";
import orderCore from "../../core/order.core";
import { useTranslation } from "../../core/translation.core";

interface OrderStatsProps {
  date?: {
    from: string;
    to: string;
  };
}

export default function OrderStats({ date }: OrderStatsProps) {
  const { t, msg } = useTranslation();

  const { data } = useQuery({
    queryFn: () => {
      return orderCore.orderStats({
        date,
      });
    },
    queryKey: ["orderStats", date],
  });

  const STATS = [
    {
      percentageChange: data?.salesPercentageChange,
      title: t(msg.order.totalSales),
      value: data?.totalSales,
    },
    {
      isCurrency: false,
      percentageChange: data?.ordersPercentageChange,
      title: t(msg.order.totalOrder),
      value: data?.totalOrders,
    },
    {
      isCurrency: false,
      percentageChange: data?.usersPercentageChange,
      title: t(msg.order.totalCustomer),
      value: data?.totalUsers,
    },
  ];

  // Determine the date range display
  let periodDescription = t(msg.order.allTime);

  if (date?.from && date?.to) {
    const fromDate = new Date(date.from);
    const toDate = new Date(date.to);

    if (fromDate.toDateString() === toDate.toDateString()) {
      periodDescription = "Today";
    } else {
      const daysDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
      const formattedDateRange = `${format(fromDate, "dd MMM")} - ${format(toDate, "dd MMM")}`;
      periodDescription = `Last ${daysDiff} days (${formattedDateRange})`;
    }
  }

  return (
    <div className="flex gap-8 overflow-x-scroll">
      {STATS.map((stat) => (
        <div key={stat.title} className="rounded-md border p-3 lg:p-5 min-w-[300px] lg:min-w-[400px] bg-white dark:bg-pos-dark  space-y-5">
          <h1 className="text-xl font-semibold">{stat.title}</h1>

          <h4>{periodDescription}</h4>

          <section className="flex justify-between items-center">
            <div className="text-sm">
              {stat.percentageChange > 0 ? (
                <span className="flex gap-2 items-center text-sm">
                  {positiveIcon} {Number(stat.percentageChange).toFixed(1)}% {t(msg.order.moreThanYesterday)}
                </span>
              ) : (
                <span className="flex gap-2 items-center text-sm">
                  {negativeIcon} {Number(stat.percentageChange).toFixed(1)}% {t(msg.order.lessThanYesterday)}
                </span>
              )}
            </div>

            <p className="text-base lg:text-xl font-bold">
              {stat.isCurrency === false ? <span>{stat.value}</span> : <span>{formatCurrency(stat.value, "HKD")}</span>}
            </p>
          </section>
        </div>
      ))}
    </div>
  );
}

const positiveIcon = (
  <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.3125 11.2503L5.25 7.03156L7.76183 9.72281C8.48804 8.18916 9.68597 6.97101 11.1533 6.27406L12.7517 5.51156M12.7517 5.51156L9.28667 4.08594M12.7517 5.51156L11.4217 9.22406"
      stroke="#12AD0E"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const negativeIcon = (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.97852 4L5.91602 7.9375L8.41618 5.43733C9.64639 6.37433 10.53 7.69385 10.928 9.18817L11.3807 10.8787M11.3807 10.8787L13.2368 7.66392M11.3807 10.8787L8.16593 9.0225"
      stroke="#ED0404"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
